import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { getFilterDates, Route } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import FilterService from '@philips-emr/hpms-stipulator-commons/lib/services/filter-service';
import { HpmsCustomCardEvent } from '@philips-emr/hpms-stipulator-components';
import { HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { FetchAPI, FilterAPI, Token } from '@philips-emr/tws-components';
import { Type } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnClear, OnDynamicContentRender, OnMount, OnSubmit, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { ClearEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { MessageService } from '@philips-emr/tws-core/lib/services';

import { HistoricoSolicitacoesAtendimentoAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';
import { getLoteStampBySituacao, LoteSituacao } from '../lote/lote-utils';

import './historico-atendimento.css';

enum SituacaoAtendimento {
  FINALIZADO = 'F',
  ANALISE = 'A',
  PENDENTE = 'P',
}

interface Data {
  dsSituacao: string;
  ieSituacao: SituacaoAtendimento;
  ieIntercambio: string;
  nrSeqAuditoria: number;
}

const OptionKey = {
  DETALHES: 'detalhes',
};

@Controller({ fragment: FragmentNames.HISTORICO_SOLICITACOES_ATENDIMENTO })
class ConsultaHistoricoSolicitacaoAtendimentoController extends BaseController {

  @Inject(Aliases.FETCH_CONSULTA)
  private fetchAPI: FetchAPI;

  @OnMount(Aliases.FILTER_CONSULTA)
  onFilterMount(event: MountEvent): void {
    FilterService.restoreFilterState(event);
  }

  @OnUnmount(Aliases.FILTER_CONSULTA)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }

  @OnClear(Aliases.FILTER_CONSULTA)
  public onClearFilter(event: ClearEvent): void {
    const api = event.target as unknown as FilterAPI;
    api.setData({});
    api.setData(getFilterDates());
  }

  @OnSubmit(Aliases.FILTER_CONSULTA)
  onSubmitFilter(event: SubmitEvent): void {
    const { record, target } = event;
    const api = target as unknown as FilterAPI;

    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    if (nrSeqSubestipulante) {
      nrSeqSubestipulante && this.fetchAPI.setParameters({ ...this.fetchAPI.getParameters(), nrSeqSubestipulante });

      if (record && record.dtInicio && record.dtFim) {
        return;
      }

      const { dtInicio, dtFim } = getFilterDates();
      !record.dtInicio && (record.dtInicio = dtInicio);
      !record.dtFim && (record.dtFim = dtFim);

      api.setData({});
      api.setData({ ...api.getRecord(), dtInicio: record.dtInicio, dtFim: record.dtFim });
    }
  }

  @OnDynamicContentRender([Aliases.HPMS_CUSTOM_CARD, Aliases.HPMS_DYNAMIC_CONTENT_SITUACAO_MOBILE])
  onDynamicContentRenderIntercambio(event: RenderEvent): void {
    const { data, render } = event;
    const { ieIntercambio } = data as Data;

    if (ieIntercambio == 'S') {
      render(() => (
        <div className="u-token-solicitacoes-pendentes">
          <Token value={MessageService.translate('stipulator.intercambio')} type={Type.NOTICE} />
        </div>
      ));
    }
  }

  @OnDynamicContentRender([Aliases.HPMS_DYNAMIC_CONTENT_SITUACAO, Aliases.HPMS_CUSTOM_CARD_MOBILE])
  onDynamicContentRenderFieldSituacao(event: RenderEvent): void {
    const { data, render } = event;
    const { dsSituacao, ieSituacao } = data as Data;

    const situacaoMap = {
      [SituacaoAtendimento.FINALIZADO]: LoteSituacao.INTEGRADO_A_PROPOSTA,
      [SituacaoAtendimento.PENDENTE]: LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO,
      [SituacaoAtendimento.ANALISE]: LoteSituacao.COM_INCONSISTENCIAS,
    };

    const content = (): ReactElement => (getLoteStampBySituacao(situacaoMap[ieSituacao], dsSituacao));

    render(content);
  }

  @HPMSOnOptionClick([Aliases.HPMS_CUSTOM_CARD, Aliases.HPMS_CUSTOM_CARD_MOBILE])
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSeqAuditoria } = data as unknown as Data;

    const action = {
      [OptionKey.DETALHES]: () => this.detalhes(nrSeqAuditoria),
    };

    if (action[key]) {
      action[key]();
    }
  }

  private detalhes(nrSeqAuditoria: number): void {
    this.historyService.push(Route.HISTORICO_SOLICITACOES_ATENDIMENTO_DETALHES, { nrSeqAuditoria });
  }
}

export default ConsultaHistoricoSolicitacaoAtendimentoController;
